import { X } from 'lucide-react';
import type { AvailabilityApprovalFiltersState } from '../../types/admin';
import { useTheme } from '../../context/ThemeContext';

interface AvailabilityApprovalFiltersProps {
  open: boolean;
  filters: AvailabilityApprovalFiltersState;
  onChange: (filters: AvailabilityApprovalFiltersState) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: () => void;
}

export const getActiveAvailabilityFilterCount = (filters: AvailabilityApprovalFiltersState) =>
  Object.values(filters).filter((value) => value !== '').length;

export default function AvailabilityApprovalFilters({
  open,
  filters,
  onChange,
  onApply,
  onReset,
  onClose,
}: AvailabilityApprovalFiltersProps) {
  const { theme } = useTheme();
  if (!open) return null;

  const controlStyle = { backgroundColor: theme.surfaceHover, borderColor: theme.border, color: theme.text };

  const updateFilter = (key: keyof AvailabilityApprovalFiltersState, value: string) => {
    onChange({
      ...filters,
      [key]: value,
    });
  };

  return (
    <div className="fixed inset-0 z-[80] flex justify-end bg-black/40" role="dialog" aria-modal="true">
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Approval Status</label>
            <select
              value={filters.approvalStatus}
              onChange={(event) => updateFilter('approvalStatus', event.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
              style={controlStyle}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Submitted From</label>
              <input
                type="date"
                value={filters.submittedFrom}
                onChange={(event) => updateFilter('submittedFrom', event.target.value)}
                className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
                style={controlStyle}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Submitted To</label>
              <input
                type="date"
                value={filters.submittedTo}
                onChange={(event) => updateFilter('submittedTo', event.target.value)}
                className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
                style={controlStyle}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Hours Per Week</label>
            <input
              type="number"
              min="0"
              value={filters.hoursPerWeek}
              onChange={(event) => updateFilter('hoursPerWeek', event.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
              style={controlStyle}
              placeholder="Hours per week"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Availability Type</label>
            <input
              value={filters.availabilityType}
              onChange={(event) => updateFilter('availabilityType', event.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-brand-primary"
              style={controlStyle}
              placeholder="Availability type"
            />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onReset}
            className="h-11 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onApply}
            className="h-11 rounded-lg bg-brand-primary text-sm font-semibold text-white"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
